import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { Checkbox } from '@/components/elements/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Textarea } from '@/components/elements/textarea'
import { Modal } from '@/components/Modal'
import { getAllEntities } from '@/services/entities.service'
import {
  createGlobalExternalCertificationCategory,
  updateGlobalExternalCertificationCategory,
} from '@/services/external_certifications.service'
import { toast } from '@/utils/helpers'

const CATEGORY_TYPES = [
  { value: 'Global', label: 'Global' },
  { value: 'Entity', label: 'Account' },
]

const AddEditCategoryModal = ({ editCategory, resetAndClose }) => {
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      categoryType: 'Global',
      entity: '',
      isActive: true,
    },
  })

  const categoryType = useWatch({ control: form.control, name: 'categoryType' })

  const resetForm = () => {
    form.reset({
      name: '',
      description: '',
      categoryType: 'Global',
      entity: '',
      isActive: true,
    })
    resetAndClose()
  }

  useEffect(() => {
    if (editCategory) {
      form.reset({
        name: editCategory.name || '',
        description: editCategory.description || '',
        categoryType: editCategory.categoryType || 'Global',
        entity: editCategory.entity?.id || editCategory.entity || '',
        isActive: editCategory.isActive ?? true,
      })
    }
  }, [editCategory, form])

  // Fetch entities for dropdown
  const { data: entitiesData } = useQuery({
    queryKey: ['entities-all'],
    queryFn: getAllEntities,
  })
  const entities = entitiesData?.results || entitiesData || []

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { isPending, mutate: mutateCategory } = useMutation({
    mutationFn: (data) => {
      // If Global, don't send entity
      const payload = {
        ...data,
        entity: data.categoryType === 'Global' ? null : data.entity,
      }
      return editCategory
        ? updateGlobalExternalCertificationCategory(editCategory.id, payload)
        : createGlobalExternalCertificationCategory(payload)
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} ${editCategory ? 'updated' : 'added'} successfully`)
      resetForm()
      queryClient.invalidateQueries({ queryKey: ['external-certification-categories-admin'] })
    },
  })

  const onSubmit = async (data) => mutateCategory(data)

  return (
    <Modal
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        {
          label: `${editCategory ? 'Update' : 'Add'} Category`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input id="name" placeholder="e.g. CPR Certification" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'Name is required' }}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      id="description"
                      placeholder="Describe this credential category"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              disabled={isPending}
              name="categoryType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CATEGORY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Global: Available to all accounts. Account: Only available to a specific
                    account.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {categoryType === 'Entity' && (
              <FormField
                control={form.control}
                disabled={isPending}
                name="entity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ? String(field.value) : ''}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {entities.map((entity) => (
                          <SelectItem key={entity.id} value={String(entity.id)}>
                            {entity.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: categoryType === 'Entity' ? 'Account is required' : false }}
              />
            )}

            <FormField
              control={form.control}
              disabled={isPending}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive categories will not be available for new credential submissions.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editCategory ? 'Update' : 'Add'} Credential Category`}
      setOpen={resetForm}
    />
  )
}

AddEditCategoryModal.propTypes = {
  editCategory: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditCategoryModal
